import React, { useMemo } from 'react';
import { KnowledgeBaseDocument } from '../../api/knowledge-base';
import {
  FileTextIcon,
  TrashIcon,
  EditIcon,
  SearchIcon,
} from 'lucide-react';
import { CONTENT_TYPE_LABELS, CONTENT_TYPE_COLORS } from '../../types/knowledge-base';
import { knowledgeBaseDocumentApi } from '../../api/knowledge-base';
import { getSearchSnippet, splitTextForHighlight } from '../../utils/search';

interface DocumentListProps {
  documents: KnowledgeBaseDocument[];
  loading: boolean;
  onDocumentSelect: (document: KnowledgeBaseDocument) => void;
  onDocumentDeleted: () => void;
  searchQuery?: string;
}

// Component to render highlighted text
const HighlightedText: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  const parts = useMemo(() => splitTextForHighlight(text, query), [text, query]);

  return (
    <>
      {parts.map((part, index) => (
        <span
          key={index}
          className={part.isHighlight ? 'bg-yellow-200 text-yellow-900 px-0.5 rounded font-medium' : undefined}
        >
          {part.text}
        </span>
      ))}
    </>
  );
};

export const DocumentList: React.FC<DocumentListProps> = ({
  documents,
  loading,
  onDocumentSelect,
  onDocumentDeleted,
  searchQuery = '',
}) => {
  const handleDelete = async (e: React.MouseEvent, documentId: string) => {
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this document?')) {
      try {
        await knowledgeBaseDocumentApi.delete(documentId);
        onDocumentDeleted();
      } catch (error) {
        console.error('Failed to delete document:', error);
        alert('Failed to delete document');
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <FileTextIcon className="w-16 h-16 mb-4 text-gray-300" />
        <p>No documents found</p>
        <p className="text-sm">Create a new document or upload a file to get started</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tags / Visa Types
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Modified
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {documents.map((document) => (
              <tr
                key={document.id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => onDocumentSelect(document)}
              >
                <td className="px-6 py-4 align-top">
                  <div className="flex items-start">
                    <FileTextIcon className="w-5 h-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                    <div className="text-sm min-w-0">
                      <div className="font-medium text-gray-900 break-words leading-snug">
                        {searchQuery ? (
                          <HighlightedText text={document.title} query={searchQuery} />
                        ) : (
                          document.title
                        )}
                      </div>
                      {document.folder && (
                        <div className="text-xs text-gray-500 break-all leading-tight">{document.folder.path}</div>
                      )}
                      {/* Show content snippet with highlighting when searching */}
                      {searchQuery && document.content && (
                        <div className="mt-1.5 text-xs text-gray-600 bg-gray-50 rounded px-2 py-1.5 border-l-2 border-blue-300">
                          <div className="flex items-center gap-1 text-gray-400 mb-0.5">
                            <SearchIcon className="w-3 h-3" />
                            <span className="text-[10px] uppercase tracking-wide">Match in content</span>
                          </div>
                          <div className="line-clamp-2">
                            {(() => {
                              const snippet = getSearchSnippet(document.content, searchQuery, 150);
                              return snippet ? (
                                <HighlightedText text={snippet} query={searchQuery} />
                              ) : null;
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span
                    className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      CONTENT_TYPE_COLORS[document.contentType as keyof typeof CONTENT_TYPE_COLORS] ||
                      'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {CONTENT_TYPE_LABELS[document.contentType as keyof typeof CONTENT_TYPE_LABELS] ||
                      document.contentType}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-1">
                    {document.visaTypes.slice(0, 2).map((visa) => (
                      <span
                        key={visa}
                        className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                      >
                        {visa}
                      </span>
                    ))}
                    {(document.visaTypes.length > 2 || document.tags.length > 0) && (
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                        +{document.visaTypes.length - 2 + document.tags.length}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                  {formatDate(document.updatedAt)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDocumentSelect(document);
                      }}
                      className="text-blue-600 hover:text-blue-900"
                      title="Edit"
                    >
                      <EditIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => handleDelete(e, document.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
